import React from 'react';
import { FaSync, FaExclamationCircle } from 'react-icons/fa';

// The semantic pill family from DESIGN.md, keyed by the status Xero reports. Green is reserved for
// a genuinely good outcome, so a voided or deleted document reads red rather than "success".
const XERO_STATUS_TONES = {
  PAID: 'bg-green-100 text-green-800',
  AUTHORISED: 'bg-blue-100 text-blue-800',
  SUBMITTED: 'bg-blue-100 text-blue-800',
  DRAFT: 'bg-yellow-100 text-yellow-800',
  VOIDED: 'bg-red-100 text-red-800',
  DELETED: 'bg-red-100 text-red-800',
};

const NEUTRAL_TONE = 'bg-gray-100 text-gray-800';
const DANGER_TONE = 'bg-red-100 text-red-800';
const POSITIVE_TONE = 'bg-green-100 text-green-800';

const titleCase = (value = '') => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

/**
 * The "Invoice Xero" / "RCTI Xero" cell.
 *
 * Once a document is in Xero, Xero's own status is the useful fact and "Success" is just noise -
 * having a Xero status at all means the push worked. Dropping it keeps the pill to one line and
 * makes the label match the Xero Status filter's vocabulary ("Authorised", "Paid", "Voided") and
 * the invoice/RCTI details pages, which already render it this way.
 */
const XeroStatusBadge = ({ submissionStatus, invoiceStatus, isSyncing = false, onClick }) => {
  if (!submissionStatus) {
    return <span className="text-gray-400">-</span>;
  }

  const failed = submissionStatus === 'failed' || submissionStatus === 'auto_sync_failed';
  const syncedToXero = submissionStatus === 'success' && invoiceStatus;

  let label;
  let tone;
  let description;

  if (failed) {
    label = 'Failed';
    tone = DANGER_TONE;
    description = 'Could not be sent to Xero';
  } else if (syncedToXero) {
    label = titleCase(invoiceStatus);
    tone = XERO_STATUS_TONES[invoiceStatus.toUpperCase()] || NEUTRAL_TONE;
    description = `In Xero as ${invoiceStatus}`;
  } else if (submissionStatus === 'success') {
    label = 'Sent';
    tone = POSITIVE_TONE;
    description = 'Sent to Xero, awaiting its status';
  } else {
    label = titleCase(submissionStatus);
    tone = NEUTRAL_TONE;
    description = `Xero submission ${submissionStatus}`;
  }

  const pillClasses = `px-2 py-1 inline-flex items-center whitespace-nowrap text-xs leading-5 font-semibold rounded-full ${tone}`;

  return (
    <span className="inline-flex items-center">
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          title={description}
          className={`${pillClasses} cursor-pointer focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-1`}
        >
          {failed && <FaExclamationCircle className="mr-1" aria-hidden="true" />}
          {label}
        </button>
      ) : (
        <span className={pillClasses} title={description}>
          {failed && <FaExclamationCircle className="mr-1" aria-hidden="true" />}
          {label}
        </span>
      )}

      {/* The last known status stays readable while it is re-checked, rather than the whole list
          blanking out and reloading underneath the user. */}
      {isSyncing && (
        <span className="ml-1.5 inline-flex items-center text-jims-cyan-dark" title="Checking Xero for updates">
          <FaSync className="animate-spin" size={10} aria-hidden="true" />
          <span className="sr-only">Checking Xero for updates</span>
        </span>
      )}
    </span>
  );
};

export default XeroStatusBadge;
